/**
 * SetLogger — compact per-set weight/reps logger with a themed calendar.
 *
 * UX:
 *  • One visible row at a time: Set dropdown + lb × reps + Save/Update
 *  • Themed gold/black calendar (Shadcn Popover + Calendar) matching the app
 *  • Picking a past date loads the saved sets for that date → user can see
 *    what they lifted on 04/15, compare to today, and confirm progression
 *  • Logged dates are dotted in the calendar + shown as quick-jump pills
 *  • Date picker also drives `logged_at` when saving, so backlogged workouts
 *    land on the correct day in the trend chart / PRs
 */
import { useEffect, useState, useCallback } from 'react';
import { Trophy, History, Loader2, Pencil, Check, Copy, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { Stepper, SetBubbles } from './SetLoggerParts';
import { RestCountdown } from './RestCountdown';
import { PlateCalculator, isBarbellExercise } from './PlateCalculator';
import { lbToUnit, unitToLb, UNIT_STEP } from './units';
import { NudgeBanner, AutoRestToggle } from './NudgeBanner';

const AUTO_REST_KEY = 'setlogger-auto-rest';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Calendar } from '../ui/calendar';

const toIsoDate = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${m}-${dd}`;
};

const fromIsoDate = (iso) => {
  if (!iso) return new Date();
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const emptyRow = () => ({
  weight: '', reps: '', logged: false, saving: false, editing: false, logId: null,
});

const UnitToggle = ({ unit, onChange }) => (
  <div className="flex rounded-full border border-white/10 p-0.5" role="group" aria-label="Weight unit">
    {['lb', 'kg'].map((u) => (
      <button
        type="button" key={u} onClick={() => onChange(u)}
        className={`px-1.5 rounded-full text-[9px] leading-4 font-semibold uppercase transition-colors ${u === unit ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
        data-testid={`setlogger-unit-${u}`}
      >
        {u}
      </button>
    ))}
  </div>
);

export default function SetLogger({
  exerciseName,
  workoutId,
  workoutTitle,
  initialSets = 3,
  targetWeightLbs = null,
  targetReps = null,
  restSeconds = 60,
  mainTimerRunning = false,
}) {
  const { api, user, refreshUser } = useAuth();
  const [unit, setUnit] = useState(user?.weight_unit === 'kg' ? 'kg' : 'lb');
  const setCount = Math.max(1, Math.min(50, Number(initialSets) || 3));
  // Parse target reps — accepts "12", "8-12", "8 to 12" etc. Picks the first integer.
  const parsedTargetReps = (() => {
    if (targetReps == null) return null;
    const m = String(targetReps).match(/\d+/);
    return m ? Number(m[0]) : null;
  })();
  const [rows, setRows] = useState(() => Array.from({ length: setCount }, emptyRow));
  const [active, setActive] = useState(0);
  const [date, setDate] = useState(toIsoDate(new Date()));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loggedDates, setLoggedDates] = useState([]); // YYYY-MM-DD strings
  const [history, setHistory] = useState({ last_session: null, personal_record: null });
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [autoRest, setAutoRest] = useState(() => localStorage.getItem(AUTO_REST_KEY) !== 'off');
  const [resting, setResting] = useState(null); // { seconds, nextSet } while counting down
  const [nudge, setNudge] = useState(null);
  const [nudgeUsed, setNudgeUsed] = useState(false);

  const todayIso = toIsoDate(new Date());
  const isViewingPast = date !== todayIso && loggedDates.includes(date);

  // Keep row count in sync with admin-configured set count.
  useEffect(() => {
    setRows((rs) => {
      if (rs.length === setCount) return rs;
      if (rs.length > setCount) return rs.slice(0, setCount);
      return [...rs, ...Array.from({ length: setCount - rs.length }, emptyRow)];
    });
    if (active >= setCount) setActive(Math.max(0, setCount - 1));
  }, [setCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch history + all dates the user has logged this exercise.
  const loadSummary = useCallback(async () => {
    try {
      const [lastRes, datesRes] = await Promise.all([
        api().get(`/strength/last/${encodeURIComponent(exerciseName)}`),
        api().get(`/strength/dates-logged/${encodeURIComponent(exerciseName)}`),
      ]);
      setHistory(lastRes.data || { last_session: null, personal_record: null });
      setLoggedDates(datesRes.data?.dates || []);
    } catch {
      setHistory({ last_session: null, personal_record: null });
      setLoggedDates([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [api, exerciseName]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!parsedTargetReps) return;
    api().get(`/strength/nudge/${encodeURIComponent(exerciseName)}`, {
      params: { target_reps: parsedTargetReps, target_sets: setCount, today: toIsoDate(new Date()) },
    }).then((res) => setNudge(res.data?.suggest ? res.data : null)).catch(() => setNudge(null));
  }, [api, exerciseName, parsedTargetReps, setCount]);

  const toggleAutoRest = () => setAutoRest((on) => {
    localStorage.setItem(AUTO_REST_KEY, on ? 'off' : 'on');
    if (on) setResting(null);
    return !on;
  });

  const endRest = useCallback(() => setResting(null), []);

  const nudgeCurrent = nudge ? lbToUnit(nudge.current_weight, unit) : 0;
  const nudgeTarget = nudge ? (unit === 'kg' ? nudgeCurrent + UNIT_STEP.kg : nudge.suggested_weight) : 0;

  const switchUnit = async (next) => {
    if (next === unit) return;
    setRows((rs) => rs.map((r) => ({ ...r, weight: r.weight === '' ? '' : String(lbToUnit(unitToLb(r.weight, unit), next)) })));
    setUnit(next);
    try {
      await api().put('/auth/profile', { weight_unit: next });
      refreshUser?.();
    } catch {
      toast.error('Could not save unit preference');
    }
  };

  const applyNudge = () => {
    setRows((rs) => rs.map((r) => (r.logged ? r : { ...r, weight: String(nudgeTarget) })));
    setNudgeUsed(true);
  };

  // When user selects a past (already-logged) date, rehydrate the rows with
  // what was actually saved that day so they can see / edit it.
  const loadDate = useCallback(async (iso) => {
    if (iso === todayIso) {
      // Reset to empty-with-last-session prefill behaviour.
      setRows(Array.from({ length: setCount }, emptyRow));
      setActive(0);
      return;
    }
    try {
      const res = await api().get(
        `/strength/sets-on-date/${encodeURIComponent(exerciseName)}?date=${iso}`
      );
      const sets = res.data?.sets || [];
      if (sets.length === 0) {
        setRows(Array.from({ length: setCount }, emptyRow));
        setActive(0);
        return;
      }
      // Build a row per saved set; fill up to admin's setCount with empties.
      const hydrated = Array.from({ length: Math.max(setCount, sets.length) }, (_, i) => {
        const s = sets[i];
        if (!s) return emptyRow();
        return {
          weight: String(lbToUnit(s.weight_lbs, unit)),
          reps: String(s.reps),
          logged: true,
          saving: false,
          editing: false,
          logId: s.id,
        };
      });
      setRows(hydrated);
      setActive(0);
    } catch (err) {
      toast.error('Could not load that date');
    }
  }, [api, exerciseName, setCount, todayIso, unit]);

  const onDatePick = (nextIso) => {
    if (!nextIso) return;
    setDate(nextIso);
    setPickerOpen(false);
    loadDate(nextIso);
  };

  const last = history.last_session;
  const pr = history.personal_record;
  const current = rows[active] || { weight: '', reps: '', logged: false };

  // Pre-fill first-empty sets from last session when we're on today.
  useEffect(() => {
    if (date !== todayIso) return;
    if (!last?.sets?.length) return;
    setRows((rs) => rs.map((r, i) => {
      if (r.weight !== '' || r.logged) return r;
      const s = last.sets[i];
      if (!s) return r;
      return { ...r, weight: String(lbToUnit(s.weight_lbs || '', unit)), reps: '' };
    }));
  }, [last?.logged_at, date, todayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateActive = (key, val) => setRows((rs) => rs.map((r, idx) => idx === active ? { ...r, [key]: val } : r));

  const saveActive = async () => {
    const row = rows[active];
    const weight = Number(row.weight);
    const weightLbs = unitToLb(weight, unit);
    // Reps source: explicit row.reps (e.g. hydrated from a past saved set the user is editing)
    // falls back to the workout's target reps (12 reps × 4 sets from the exercise card).
    const repsRaw = row.reps !== '' ? row.reps : parsedTargetReps;
    const reps = Number(repsRaw);
    if (!row.weight || Number.isNaN(weight) || Number.isNaN(reps) || reps <= 0) {
      toast.error(parsedTargetReps ? 'Enter weight first' : 'Enter weight and target reps first');
      return;
    }
    if (row.saving) return;
    setRows((rs) => rs.map((r, i) => i === active ? { ...r, saving: true } : r));
    try {
      const res = row.logId
        ? await api().patch(`/strength/log/${row.logId}`, { weight_lbs: weightLbs, reps })
        : await api().post('/strength/log', {
            exercise_name: exerciseName,
            weight_lbs: weightLbs,
            reps,
            set_index: active + 1,
            workout_id: workoutId || null,
            workout_title: workoutTitle || null,
            logged_at: date || null,
          });
      setRows((rs) => rs.map((r, i) => i === active
        ? { ...r, weight: String(weight), reps: String(reps), logged: true, editing: false, saving: false, logId: res.data?.log?.id || r.logId }
        : r));
      if (res.data?.is_pr) {
        const delta = res.data.delta;
        toast.success(
          delta != null
            ? `🏆 NEW PR! +${lbToUnit(delta, unit)} ${unit} on ${exerciseName}`
            : `🏆 NEW PR on ${exerciseName}!`,
          { duration: 6000 }
        );
      } else {
        toast.success(row.logId
          ? `Set ${active + 1} updated · ${weight} ${unit} × ${reps}`
          : `Set ${active + 1} saved · ${weight} ${unit} × ${reps} on ${date}`);
      }
      // Refresh logged-dates list so the calendar dot appears on first save.
      loadSummary();
      const next = rows.findIndex((r, i) => i !== active && !r.logged);
      if (next !== -1) {
        setActive(next);
        if (autoRest && !mainTimerRunning && !row.logId && date === todayIso && restSeconds > 0) {
          setResting({ seconds: restSeconds, nextSet: next + 1 });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save set');
      setRows((rs) => rs.map((r, i) => i === active ? { ...r, saving: false } : r));
    }
  };

  const editActive = () => setRows((rs) => rs.map((r, i) => i === active ? { ...r, logged: false, editing: true } : r));

  const weightPlaceholder = !last && targetWeightLbs ? `${lbToUnit(targetWeightLbs, unit)}` : '';

  // react-day-picker `modifiers` — marks days the user has previously logged.
  const loggedDateObjs = loggedDates.map(fromIsoDate);

  const prevRow = rows[active - 1];
  const dateLabel = date === todayIso ? 'Today' : format(fromIsoDate(date), 'MMM d');

  return (
    <div className="bg-[#0b0b0b] border border-white/10 rounded-3xl p-4 mt-3" data-testid={`setlogger-${exerciseName}`}>
      {/* Chips: last session · PR · date */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {loadingHistory ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-white/40" />
        ) : last ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.05] text-[11px] text-white/70" data-testid="setlogger-last-chip">
            <History className="h-3 w-3" /> <b className="text-white">{lbToUnit(last.sets[0].weight_lbs, unit)} {unit} × {last.sets[0].reps}</b>
          </span>
        ) : targetWeightLbs ? (
          <span className="px-2.5 py-1 rounded-full bg-white/[0.05] text-[11px] text-white/70">Target: <b className="text-white">{lbToUnit(targetWeightLbs, unit)} {unit}</b></span>
        ) : (
          <span className="px-2.5 py-1 rounded-full bg-primary/10 text-[11px] text-primary" data-testid="setlogger-first-time-chip">First time. Set your baseline</span>
        )}
        {pr && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-400/10 text-[11px] text-yellow-300" data-testid="setlogger-pr-chip">
            <Trophy className="h-3 w-3" /> PR {lbToUnit(pr.weight_lbs, unit)} {unit} × {pr.reps}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={` inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                isViewingPast ? 'border-primary text-primary bg-primary/10' : 'border-white/10 text-white/70 hover:border-primary/50'
              }`}
              data-testid="setlogger-date-trigger"
            >
              <CalendarIcon className="h-3 w-3" /> {dateLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="p-0 bg-card border border-primary/40 rounded-xl shadow-[0_0_32px_rgba(255,215,0,0.18)]"
            data-testid="setlogger-date-popover"
          >
            <div className="bg-primary/15 border-b border-primary/30 px-4 py-3 rounded-t-xl">
              <p className="text-[10px] uppercase tracking-widest text-primary/80">Workout date</p>
              <p className="font-heading text-lg text-white">{format(fromIsoDate(date), 'EEE, MMM d yyyy')}</p>
            </div>
            <Calendar
              mode="single"
              selected={fromIsoDate(date)}
              onSelect={(d) => onDatePick(d ? toIsoDate(d) : null)}
              disabled={{ after: new Date() }}
              initialFocus
              modifiers={{ logged: loggedDateObjs }}
              modifiersClassNames={{
                logged: 'relative after:content-[""] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-primary',
              }}
              className="bg-card text-white"
            />
            <div className="border-t border-border px-3 py-2 flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" /> logged day
              </span>
              <button onClick={() => onDatePick(todayIso)} className="text-primary hover:text-yellow-300 font-heading tracking-wider uppercase" data-testid="setlogger-date-today">
                Today
              </button>
            </div>
          </PopoverContent>
        </Popover>
        </div>
      </div>

      {/* Quick-jump pills for recent logged dates */}
      {loggedDates.length > 0 && (
        <div className="flex items-center gap-1 overflow-x-auto mb-3 -mx-1 px-1 pb-1">
          {loggedDates.slice(0, 6).map((iso) => (
            <button
              key={iso}
              onClick={() => onDatePick(iso)}
              className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-heading tracking-wider uppercase border transition-colors ${
                iso === date ? 'bg-primary text-black border-primary' : 'bg-white/5 text-white/60 border-white/10 hover:border-primary/50'
              }`}
              data-testid={`setlogger-date-pill-${iso}`}
            >
              {format(fromIsoDate(iso), 'MMM d')}
            </button>
          ))}
        </div>
      )}

      {nudge && !nudgeUsed && date === todayIso && !rows.some((r) => r.logged && unitToLb(r.weight, unit) > nudge.current_weight + 0.01) && (
        <NudgeBanner nudge={nudge} unit={unit} current={nudgeCurrent} target={nudgeTarget} onUse={applyNudge} />
      )}

      <SetBubbles rows={rows} active={active} onPick={(i) => { setResting(null); setActive(i); }} />

      {resting ? (
        <RestCountdown key={`${resting.nextSet}-${resting.seconds}`} seconds={resting.seconds} nextSet={resting.nextSet} onDone={endRest} />
      ) : current.logged ? (
        <div className="flex items-center justify-between rounded-2xl bg-green-500/10 border border-green-500/30 px-4 py-4" data-testid="setlogger-logged-summary">
          <span className="text-green-400 font-semibold flex items-center gap-2 text-sm">
            <Check className="h-4 w-4" /> Set {active + 1}: {current.weight} {unit} × {current.reps}
          </span>
          <button type="button" onClick={editActive} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="setlogger-edit">
            <Pencil className="h-3 w-3" /> Edit
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <Stepper
              label="Weight" unit={unit} step={UNIT_STEP[unit]} value={current.weight} placeholder={weightPlaceholder}
              headerRight={<UnitToggle unit={unit} onChange={switchUnit} />}
              onChange={(v) => updateActive('weight', v)} testId="setlogger-weight"
            />
            <Stepper
              label="Reps" step={1} value={current.reps} placeholder={parsedTargetReps != null ? String(parsedTargetReps) : ''}
              onChange={(v) => updateActive('reps', v)} testId="setlogger-reps"
            />
          </div>
          {isBarbellExercise(exerciseName) && (
            <PlateCalculator weight={current.weight || weightPlaceholder} unit={unit} />
          )}
          {prevRow?.logged && (
            <button
              type="button"
              onClick={() => setRows((rs) => rs.map((r, i) => (i === active ? { ...r, weight: prevRow.weight, reps: prevRow.reps } : r)))}
              className="mt-2 text-[11px] text-white/50 hover:text-primary inline-flex items-center gap-1"
              data-testid="setlogger-same-as-last"
            >
              <Copy className="h-3 w-3" /> Same as set {active}: {prevRow.weight} {unit} × {prevRow.reps}
            </button>
          )}
          <button
            type="button" onClick={saveActive} disabled={current.saving}
            className="mt-3 w-full h-12 rounded-2xl bg-primary text-black font-heading uppercase tracking-[0.18em] text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-wait"
            data-testid="setlogger-save"
          >
            {current.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `${current.editing ? 'Update' : 'Log'} set ${active + 1}`}
          </button>
        </>
      )}
      <AutoRestToggle on={autoRest} onToggle={toggleAutoRest} />
    </div>
  );
}
